import React from 'react';
import { History, RefreshCw, Trash2, UserPlus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useCalculatorEmailHistory } from '../hooks/useCalculatorEmailHistory';
import type { RecipientInfoEmail, SenderInfoEmail } from '../types/calculator-email.types';
import { formatDistanceToNow } from 'date-fns';

interface CalculatorEmailHistoryPanelProps {
  onLoadHistory: (data: { recipient: RecipientInfoEmail; sender: SenderInfoEmail }) => void;
}

export const CalculatorEmailHistoryPanel: React.FC<CalculatorEmailHistoryPanelProps> = ({
  onLoadHistory,
}) => {
  const { history, isLoading, fetchHistory, deleteEntry, convertHistoryToFormData } =
    useCalculatorEmailHistory();

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);

  const handleLoad = (id: string) => {
    const item = history.find((h) => h.id === id);
    if (item) {
      const formData = convertHistoryToFormData(item);
      onLoadHistory(formData);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <History className="h-5 w-5 text-primary" />
          Email History
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => fetchHistory()}
          disabled={isLoading}
        >
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {isLoading && history.length === 0 ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading history...
        </div>
      ) : history.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-muted-foreground text-center">
          <History className="h-10 w-10 mb-2 opacity-50" />
          <p className="text-sm">No email history yet</p>
          <p className="text-xs mt-1">Sent emails will appear here</p>
        </div>
      ) : (
        <ScrollArea className="flex-1 -mx-2">
          <div className="space-y-2 px-2">
            {history.map((item) => {
              const loanDetails = item.loan_details as any;
              const homePrice = loanDetails?.mortgageSummary?.homePrice || 0;
              const sentAt = item.sent_at ? new Date(item.sent_at) : new Date(item.created_at);

              return (
                <div
                  key={item.id}
                  className="p-3 rounded-lg border border-border bg-card hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate text-foreground">
                        {item.recipient_name}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {item.recipient_email}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-primary font-medium">
                          {formatCurrency(homePrice)}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(sentAt, { addSuffix: true })}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => handleLoad(item.id)}
                        title="Load recipient"
                      >
                        <UserPlus className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        onClick={() => deleteEntry(item.id)}
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      )}
    </div>
  );
};
